import { Injectable } from '@angular/core';

import { HttpAgent } from '@ag-ui/client';
import type { Message } from '@ag-ui/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';

import {
  injectTellentAssistantActorGetter,
  injectTellentAssistantAuthTokenGetter,
  injectTellentAssistantConfig,
} from '@core/tellent-assistant-contracts';

import { AGENT_ID, buildTellentAssistantHeaders } from './tellent-assistant-headers';

@Injectable({ providedIn: 'root' })
export class TellentAssistantAguiClient {
  private readonly config = injectTellentAssistantConfig();
  private readonly getToken = injectTellentAssistantAuthTokenGetter();
  private readonly getActor = injectTellentAssistantActorGetter({ optional: true }) ?? (() => null);

  private readonly messagesSubject = new ReplaySubject<readonly Message[]>(1);
  private readonly runErrorSubject = new Subject<{ message: string }>();
  private readonly runFinalizedSubject = new Subject<void>();
  private readonly agent = new HttpAgent({
    url: `${this.config.apiUrl}/agui/${AGENT_ID}/stream`,
    headers: this.buildHeaders(),
    agentId: AGENT_ID,
  });

  public readonly messages$: Observable<readonly Message[]> = this.messagesSubject.asObservable();
  public readonly runError$: Observable<{ message: string }> = this.runErrorSubject.asObservable();
  public readonly runFinalized$: Observable<void> = this.runFinalizedSubject.asObservable();

  constructor() {
    this.agent.subscribe({
      onMessagesChanged: ({ messages }) => this.messagesSubject.next(messages),
      onRunErrorEvent: ({ event }) => this.runErrorSubject.next({ message: event.message ?? '' }),
      onRunFinalized: () => this.runFinalizedSubject.next(),
    });
  }

  public setThreadId(threadId: string): void {
    this.agent.threadId = threadId;
  }

  public addUserMessage(text: string): void {
    this.agent.addMessages([<Message>{ id: crypto.randomUUID(), role: 'user', content: text }]);
  }

  public setMessages(messages: readonly Message[]): void {
    this.agent.setMessages([...messages]);
  }

  public async run(runId: string): Promise<void> {
    this.agent.headers = this.buildHeaders();
    try {
      await this.agent.runAgent({ runId });
    } catch {
      /* runError subject receives the error via onRunErrorEvent */
    }
  }

  public cancelRun(): void {
    this.agent.abortRun();
  }

  private buildHeaders(): Record<string, string> {
    return buildTellentAssistantHeaders(
      this.getToken(),
      this.getActor(),
      this.config.devPlayground,
    );
  }
}
